"use client";

// ─── Globalny system powiadomień (toast) ─────────────────────────
// Cele projektowe (naprawa typowych błędów toastów):
//  • Renderowanie przez portal do <body> — brak przycinania przez
//    overflow:hidden / transformowanych przodków, brak konfliktu z
//    modalami, drawerami, koszykiem i overlayami.
//  • Kontener z pointer-events:none, KAŻDA karta z pointer-events:auto —
//    toast nigdy nie blokuje kliknięć strony, a jego przyciski są w
//    pełni klikalne.
//  • Najwyższy z-index (z-[9999]) — zawsze nad pozostałym interfejsem.
//  • Auto-zamykanie z pauzą na hover/focus — komunikaty nie znikają zbyt
//    szybko, gdy użytkownik z nimi pracuje.
//  • Dostępność (WCAG): role status/alert, aria-live, obsługa klawiatury
//    (Escape), dostępny przycisk zamknięcia.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Info, X, Gift } from "lucide-react";

export type ToastVariant = "success" | "error" | "info" | "default" | "gift";

export interface ToastAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

export interface ToastOptions {
  variant?: ToastVariant;
  title?: string;
  description?: string;
  action?: ToastAction;
  /** Czas w ms do auto-zamknięcia. 0 = trwały (do ręcznego zamknięcia). */
  duration?: number;
  /** Niestandardowa ikona (nadpisuje ikonę wariantu). */
  icon?: ReactNode;
}

interface ToastItem extends Required<Pick<ToastOptions, "variant" | "duration">> {
  id: string;
  title?: string;
  description?: string;
  action?: ToastAction;
  icon?: ReactNode;
}

interface ToastContextValue {
  toast: (opts: ToastOptions) => string;
  success: (title: string, opts?: Omit<ToastOptions, "variant" | "title">) => string;
  error: (title: string, opts?: Omit<ToastOptions, "variant" | "title">) => string;
  info: (title: string, opts?: Omit<ToastOptions, "variant" | "title">) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_TOASTS = 4;
const DEFAULT_DURATION = 5000;

let counter = 0;
function nextId(): string {
  counter += 1;
  return `toast-${Date.now()}-${counter}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((opts: ToastOptions): string => {
    const id = nextId();
    const item: ToastItem = {
      id,
      variant: opts.variant ?? "default",
      duration: opts.duration ?? DEFAULT_DURATION,
      title: opts.title,
      description: opts.description,
      action: opts.action,
      icon: opts.icon,
    };
    setToasts((prev) => {
      const next = [item, ...prev];
      return next.slice(0, MAX_TOASTS);
    });
    return id;
  }, []);

  const success = useCallback(
    (title: string, opts?: Omit<ToastOptions, "variant" | "title">) =>
      toast({ ...opts, variant: "success", title }),
    [toast]
  );
  const error = useCallback(
    (title: string, opts?: Omit<ToastOptions, "variant" | "title">) =>
      toast({ ...opts, variant: "error", title }),
    [toast]
  );
  const info = useCallback(
    (title: string, opts?: Omit<ToastOptions, "variant" | "title">) =>
      toast({ ...opts, variant: "info", title }),
    [toast]
  );

  const value = useMemo<ToastContextValue>(
    () => ({ toast, success, error, info, dismiss }),
    [toast, success, error, info, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return ctx;
}

// ─── Renderowanie (portal) ───────────────────────────────────────
function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  return createPortal(
    <div
      aria-live="polite"
      aria-relevant="additions"
      className="pointer-events-none fixed inset-x-0 top-0 z-[9999] flex flex-col items-center gap-3 px-3 pt-4 sm:inset-x-auto sm:right-4 sm:top-4 sm:items-end sm:px-0"
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
}

const VARIANT_STYLES: Record<
  ToastVariant,
  { ring: string; iconWrap: string; icon: ReactNode; role: "status" | "alert"; live: "polite" | "assertive" }
> = {
  success: {
    ring: "border-emerald-200 dark:border-emerald-500/30",
    iconWrap: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400",
    icon: <CheckCircle2 size={18} />,
    role: "status",
    live: "polite",
  },
  error: {
    ring: "border-red-200 dark:border-red-500/30",
    iconWrap: "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400",
    icon: <AlertCircle size={18} />,
    role: "alert",
    live: "assertive",
  },
  info: {
    ring: "border-blue-200 dark:border-blue-500/30",
    iconWrap: "bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400",
    icon: <Info size={18} />,
    role: "status",
    live: "polite",
  },
  gift: {
    ring: "border-violet-200 dark:border-violet-500/30",
    iconWrap: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400",
    icon: <Gift size={18} />,
    role: "status",
    live: "polite",
  },
  default: {
    ring: "border-slate-200 dark:border-slate-700",
    iconWrap: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    icon: <Info size={18} />,
    role: "status",
    live: "polite",
  },
};

function ToastCard({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}) {
  const { id, variant, duration } = toast;
  const styles = VARIANT_STYLES[variant];

  // Zarządzanie timerem z pauzą na hover/focus i odliczaniem pozostałego czasu.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(duration);
  const startRef = useRef(0);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    if (!duration || duration <= 0) return;
    clearTimer();
    startRef.current = Date.now();
    timerRef.current = setTimeout(() => onDismiss(id), Math.max(0, remainingRef.current));
  }, [duration, clearTimer, id, onDismiss]);

  const pauseTimer = useCallback(() => {
    if (!duration || duration <= 0) return;
    clearTimer();
    remainingRef.current -= Date.now() - startRef.current;
  }, [duration, clearTimer]);

  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer, clearTimer]);

  return (
    <motion.div
      layout
      role={styles.role}
      aria-live={styles.live}
      initial={{ opacity: 0, y: -16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.96, transition: { duration: 0.15 } }}
      transition={{ type: "spring", stiffness: 280, damping: 24 }}
      onMouseEnter={pauseTimer}
      onMouseLeave={startTimer}
      onFocus={pauseTimer}
      onBlur={startTimer}
      onKeyDown={(e) => {
        if (e.key === "Escape") onDismiss(id);
      }}
      tabIndex={0}
      className={`pointer-events-auto w-full max-w-sm rounded-2xl border bg-white shadow-xl shadow-slate-900/10 outline-none ring-0 focus-visible:ring-2 focus-visible:ring-primary/40 dark:bg-slate-900 ${styles.ring}`}
    >
      <div className="flex items-start gap-3 p-4">
        <span
          className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${styles.iconWrap}`}
          aria-hidden="true"
        >
          {toast.icon ?? styles.icon}
        </span>

        <div className="min-w-0 flex-1">
          {toast.title && (
            <p className="text-sm font-semibold leading-snug text-slate-800 dark:text-white">
              {toast.title}
            </p>
          )}
          {toast.description && (
            <p className="mt-0.5 text-sm leading-snug text-slate-500 dark:text-slate-400">
              {toast.description}
            </p>
          )}

          {toast.action && (
            <div className="mt-2.5">
              {toast.action.href ? (
                <a
                  href={toast.action.href}
                  onClick={() => onDismiss(id)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
                >
                  {toast.action.label}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    toast.action?.onClick?.();
                    onDismiss(id);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
                >
                  {toast.action.label}
                </button>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => onDismiss(id)}
          aria-label="Zamknij powiadomienie"
          className="-mr-1 -mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
        >
          <X size={15} />
        </button>
      </div>
    </motion.div>
  );
}
